import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { PreguntaSatisfaccion, PreguntaControlConfig, EncuestaSatisfaccionProps } from 'src/common/encuesta';
import { GtagEvent } from 'src/common/gtagEvent';
import { AppState } from '../reducers';
import { ModalComponent } from '../shared/modal/modal.component';
import { AddEncuestaRequested, EncuestaPropsRequested, PreguntasRequested } from '../store/pregunta-satisfaccion/pregunta-satisfaccion.actions';
import { preguntaSatisfaccionAll, preguntaSatisfaccionEncuestaProps, preguntaSatisfaccionEncuestaReady, preguntaSatisfaccionEncuestaSaved, preguntaSatisfaccionPreguntaReady } from '../store/pregunta-satisfaccion/pregunta-satisfaccion.selectors';
import { encuestaValidateDesc } from './encuestaValidateDesc';

@Component({
	selector: 'encuesta-satisfaccion',
	templateUrl: './encuesta-satisfaccion.component.html',
	styleUrls: ['./encuesta-satisfaccion.component.scss']
})
export class EncuestaSatisfaccionComponent implements OnDestroy, AfterViewInit {

	private readonly PARENT_INDEX: number = 1;

	@ViewChild( "encuestaModal", { static: true } )
	private encuestaModal: ModalComponent;
	private valueChangesSubscription: Subscription;
	private encuestaSavedSubscription: Subscription;
	private encuestaPropsSubscription: Subscription;
	private onCompletedSubject: Subject<Boolean> = new Subject();
	private onCompleted$: Observable<Boolean> = this.onCompletedSubject.asObservable();

	encuestaForm: UntypedFormGroup;
	preguntas$: Observable<PreguntaControlConfig[]>;
	preguntaReady$: Observable<Boolean>;
	encuestaReady$: Observable<Boolean>;
	isDescDirty: Boolean = false;


	constructor( private store: Store<AppState>, private modalService: NgbModal, formBuilder: UntypedFormBuilder, private gtag: GtagEvent ) {
		this.preguntaReady$ = this.store.select( preguntaSatisfaccionPreguntaReady );
		this.encuestaReady$ = this.store.select( preguntaSatisfaccionEncuestaReady );

		this.encuestaForm = formBuilder.group({
			motivo: [ '',  [ Validators.required ] ],
			mostrar: [ '' ]
		});

		this.preguntas$ = this.store.select( preguntaSatisfaccionAll )
			.pipe(
				map( preguntas => preguntas.map( config => {
						let valueAsName = this.toDescControlName( config.preguntaAbrev );
						return { ...config, radioId: valueAsName, radioControlName: 'motivo', textControlName: valueAsName };
					})
				),
				tap( (preguntas: PreguntaControlConfig[]) => {
					preguntas.forEach( pregunta => this.encuestaForm.addControl( pregunta.textControlName, new UntypedFormControl() ) );
					this.encuestaForm.setValidators( encuestaValidateDesc() );
				})
			);

		this.valueChangesSubscription = this.encuestaForm.valueChanges
			.pipe(
				tap( value => {
					let motivo: string = value["motivo"];
					if( motivo )
						this.isDescDirty = this.encuestaForm.controls[ this.toDescControlName( motivo ) ].dirty;
				})
			).subscribe();
	}
	ngAfterViewInit(): void {
	}

	ngOnDestroy(): void {
		this.valueChangesSubscription.unsubscribe();
		if( this.encuestaSavedSubscription )
			this.encuestaSavedSubscription.unsubscribe();
		if( this.encuestaPropsSubscription )
			this.encuestaPropsSubscription.unsubscribe();
	}

	onRadioChange( event: any ) {
		let parent: any = event.path.length > this.PARENT_INDEX? event.path[ this.PARENT_INDEX ]: null;
		if( parent )
			parent.querySelector( "input[type=text]" ).focus();
	}

	onSubmit() {
		if( this.encuestaForm.valid ) {
			let preguntaAbrev: string = this.encuestaForm.controls.motivo.value;
			let descripcion: string = this.encuestaForm.controls[ this.toDescControlName( preguntaAbrev ) ].value;
			let mostrar: boolean = this.encuestaForm.controls.mostrar.value;
			this.store.dispatch( new AddEncuestaRequested( { preguntaAbrev, descripcion, mostrar } ) );

			this.encuestaSavedSubscription = this.store.select( preguntaSatisfaccionEncuestaSaved )
				.pipe(
					filter( saved => saved ),
					tap( () => {
						this.gtag.emit( preguntaAbrev.toLowerCase().replace( '_', '-' ), 'encuesta-satisfaccion' );
						this.encuestaForm.reset();
						this.encuestaModal.close()
						this.onCompletedSubject.next( true );
					})
			).subscribe();
		}
	}

	public open() {
		// Antes de abrir el modal recupero las preguntas y las propiedades de la encuesta
		this.store.dispatch( new PreguntasRequested() );
		this.store.dispatch( new EncuestaPropsRequested() );

		this.encuestaPropsSubscription = this.store.select( preguntaSatisfaccionEncuestaProps )
			.pipe(
				filter( props => props != null ),
				first(),
				tap( props => {
					this.encuestaForm.controls.mostrar.setValue( props.mostrar );
				})
			).subscribe();

		this.encuestaModal.open();
	}

	private toDescControlName( preguntaAbrev: string ): string {
		return preguntaAbrev.toLowerCase().replace( '_', '-' ) + '-desc';
	}

	public onCompleted(): Observable<Boolean> {
		return this.onCompleted$;
	}

	public onClose() {
		this.onCompletedSubject.next( false );
		this.encuestaModal.close();
	}

	public onEncuestaProps(): Observable<EncuestaSatisfaccionProps> {
		this.store.dispatch( new EncuestaPropsRequested() );
		return this.store.select( preguntaSatisfaccionEncuestaProps ).pipe(
				filter( props => props != null )
			);
	}

}
